import { useState, useCallback } from 'react';
import { UploadStep } from './components/UploadStep';
import { ReviewStep } from './components/ReviewStep';
import { DoneStep } from './components/DoneStep';
import { StatusBanner } from './components/StatusBanner';
import { scanFile, pseudonymizeFile, triggerDownload, ScanOutdatedError } from './api';
import { DEFAULT_MASK_CHAR, DEFAULT_MODE } from './utils/modes';
import prodamPrefeituraLogo from './assets/prodam-prefeitura.png';
import logo from './assets/logo.svg';
import type {
  ScanResponse,
  EntityType,
  AppStatus,
  AnonymizationMode,
} from './types/api';

type Step =
  | { name: 'upload' }
  | { name: 'review'; scan: ScanResponse }
  | { name: 'done'; filename: string; findings: number; mode: AnonymizationMode };

export default function App() {
  const [tenantId, setTenantId] = useState('demo');
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>({ name: 'upload' });
  const [disabled, setDisabled] = useState<Set<EntityType>>(new Set());
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<AnonymizationMode>(DEFAULT_MODE);
  const [maskChar, setMaskChar] = useState(DEFAULT_MASK_CHAR);
  const [scanning, setScanning] = useState(false);
  const [pseudonymizing, setPseudonymizing] = useState(false);
  const [status, setStatus] = useState<AppStatus | null>(null);

  const handleScan = useCallback(async () => {
    if (!file) return;
    setScanning(true);
    setStatus(null);
    try {
      const scan = await scanFile(file, tenantId.trim() || 'demo');
      setDisabled(new Set());
      // Initially select all detections
      setSelectedIndices(new Set(scan.detections.map((_, i) => i)));
      setStep({ name: 'review', scan });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message: msg });
    } finally {
      setScanning(false);
    }
  }, [file, tenantId]);

  const handleToggleType = useCallback((type: EntityType, enabled: boolean) => {
    setDisabled((prev) => {
      const next = new Set(prev);
      if (enabled) next.delete(type);
      else next.add(type);
      return next;
    });

    // Also update selected indices for all detections of this type
    if (step.name === 'review') {
      setSelectedIndices((prev) => {
        const next = new Set(prev);
        step.scan.detections.forEach((d, i) => {
          if (d.type === type) {
            if (enabled) next.add(i);
            else next.delete(i);
          }
        });
        return next;
      });
    }
  }, [step]);

  const handleToggleDetection = useCallback((index: number, selected: boolean) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (selected) next.add(index);
      else next.delete(index);
      return next;
    });
  }, []);

  const handlePseudonymize = useCallback(async () => {
    if (!file || step.name !== 'review') return;

    if (selectedIndices.size === 0) {
      setStatus({
        kind: 'warn',
        message: 'Nenhuma ocorrência selecionada. Selecione pelo menos uma para continuar.',
      });
      return;
    }

    setPseudonymizing(true);
    setStatus(null);
    try {
      const result = await pseudonymizeFile(
        file,
        tenantId.trim() || 'demo',
        Array.from(selectedIndices),
        mode,
        maskChar,
        step.scan.detections_hash,
      );
      triggerDownload(result.blob, result.filename);
      setStep({
        name: 'done',
        filename: result.filename,
        findings: result.findings,
        mode: result.mode,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message: msg });
      // The indices no longer point at what the reviewer saw: scan again.
      if (err instanceof ScanOutdatedError) {
        setSelectedIndices(new Set());
        setStep({ name: 'upload' });
      }
    } finally {
      setPseudonymizing(false);
    }
  }, [file, step, selectedIndices, tenantId, mode, maskChar]);

  const handleReset = useCallback(() => {
    setFile(null);
    setStep({ name: 'upload' });
    setDisabled(new Set());
    setSelectedIndices(new Set());
    setStatus(null);
  }, []);

  return (
    <div className="container">
      <header>
        <div className="logo-container">
          <img src={logo} alt="Anonime" className="logo" />
          <h1>Anonime</h1>
        </div>
        <p className="subtitle">
          Proteção inteligente de dados pessoais em conformidade com a LGPD
        </p>
      </header>

      {step.name === 'upload' && <StatusBanner status={status} />}

      {step.name === 'upload' && (
        <UploadStep
          tenantId={tenantId}
          onTenantIdChange={setTenantId}
          file={file}
          onFileSelected={setFile}
          onFileRemoved={() => setFile(null)}
          onScan={handleScan}
          scanning={scanning}
        />
      )}

      {step.name === 'review' && file && (
        <ReviewStep
          scan={step.scan}
          disabled={disabled}
          selectedIndices={selectedIndices}
          mode={mode}
          maskChar={maskChar}
          onModeChange={setMode}
          onMaskCharChange={setMaskChar}
          onToggleType={handleToggleType}
          onToggleDetection={handleToggleDetection}
          onPseudonymize={handlePseudonymize}
          onReset={handleReset}
          pseudonymizing={pseudonymizing}
          status={status}
          filename={file.name}
        />
      )}

      {step.name === 'done' && (
        <DoneStep
          filename={step.filename}
          findings={step.findings}
          mode={step.mode}
          onStartOver={handleReset}
        />
      )}

      <footer>
        <div className="footer-badge">
          Anonimização segura e consistente
        </div>
        <p style={{ marginTop: '12px' }}>
          Processamento 100% server-side · Dados protegidos em conformidade com LGPD
        </p>
        <img
          src={prodamPrefeituraLogo}
          alt="Prodam govtech sp · Prefeitura de São Paulo"
          className="footer-logos"
        />
      </footer>
    </div>
  );
}
